import { Link } from 'react-router-dom';
import logoUrl from '../../../assets/shopify/scaleads-ai-icon.jpg';

const plans = [
  {
    name: 'Starter',
    price: '$49',
    features: ['Meta campaign management', 'Daily optimiser', 'Hook library', 'First-party pixel'],
  },
  {
    name: 'Growth',
    price: '$149',
    featured: true,
    features: ['Meta campaign management', 'ScaleAds Copilot', 'Competitor intelligence', 'Benchmarks'],
  },
  {
    name: 'Scale',
    price: '$399',
    features: ['Everything in Growth', 'Agency console', 'AI Operator', 'Incrementality testing'],
  },
];

export default function Home({ focus }: { focus?: 'shopify' }) {
  return (
    <main className="marketing-shell">
      <header className="marketing-header">
        <div className="marketing-nav">
          <Link className="marketing-logo" to="/" aria-label="ScaleAds AI home">
            <img className="brand-logo-image" src={logoUrl} alt="" aria-hidden="true" />
            ScaleAds AI
          </Link>
          <nav aria-label="Main navigation">
            <a href="#how">How it works</a>
            <a href="#measurement">Measurement</a>
            <a href="#products">Products</a>
            <a href="#pricing">Pricing</a>
            <Link to="/login">Sign in</Link>
          </nav>
          <Link className="marketing-button nav-cta" to="/standalone">Start free</Link>
        </div>
      </header>

      <section className="marketing-hero" id="platform">
        <div className="marketing-wrap hero-inner">
          <p className="marketing-kicker">Meta ad automation</p>
          <h1>Meta campaigns.<br /><span>One clear system.</span></h1>
          <p className="hero-lede">
            Plan, launch and optimise Meta campaigns with merchant-controlled budgets—and understand
            what your advertising actually caused, not only what the platform claims credit for.
          </p>
          <div className="hero-actions">
            <Link className="marketing-button hero-primary" to="/standalone">Start 14-day trial</Link>
            <a className="marketing-button hero-secondary" href="#how">See how it works</a>
          </div>
          <p className="hero-note">No card required for standalone · Shopify access available by request</p>
          <div className="channel-row" aria-label="Supported advertising channel">
            <span>Meta Ads</span><span>Google · roadmap</span><span>TikTok · roadmap</span><span>Pinterest · roadmap</span><span>Reddit · roadmap</span>
          </div>
        </div>
      </section>

      <section className="marketing-section" id="how">
        <div className="marketing-wrap">
          <div className="section-heading">
            <p className="marketing-kicker">From product to live campaign</p>
            <h2>One operating loop, not five disconnected dashboards.</h2>
            <p>ScaleAds brings catalogue, creative, campaign and revenue signals into one daily workflow.</p>
          </div>
          <div className="step-grid">
            <article><span>01</span><h3>Find what is worth promoting</h3><p>Score products using margin, velocity, price point and creative readiness before allocating spend.</p></article>
            <article><span>02</span><h3>Generate and launch</h3><p>Build platform-specific hooks and campaign variants, then launch from one controlled workflow.</p></article>
            <article><span>03</span><h3>Optimise with guardrails</h3><p>Shift budget toward stronger outcomes, pause losses and require approval above the limit you set.</p></article>
          </div>
        </div>
      </section>

      <section className="marketing-section measurement-section" id="measurement">
        <div className="marketing-wrap measurement-grid">
          <div className="section-heading">
            <p className="marketing-kicker">Measurement without the theatre</p>
            <h2>Reported attribution is not always the sale you caused.</h2>
            <p>ScaleAds compares Meta reporting with your first-party conversion trail.</p>
          </div>
          <div className="measurement-card">
            <p>Every ad platform grades its own work. Overlapping attribution means several platforms can take credit for one conversion.</p>
            <p><strong>ScaleAds compares reported attribution with first-party revenue signals and holdout evidence.</strong></p>
            <p>The conservative number is usually lower. It is also the number that helps decide whether the next dollar is worth spending.</p>
          </div>
        </div>
      </section>

      <section className="marketing-section" id="products">
        <div className="marketing-wrap">
          <div className="section-heading">
            <p className="marketing-kicker">Two ways to run it</p>
            <h2>One brand. The right product for your store.</h2>
            <p>The experience is shared; authentication, billing and merchant data remain separated.</p>
          </div>
          <div className="product-choice-grid">
            <article className={`product-choice ${focus === 'shopify' ? 'featured' : ''}`}>
              <div className="choice-label shopify-label">Shopify app</div>
              <h3>Work inside Shopify</h3>
              <p>Products, orders and revenue context sync into an embedded workspace.</p>
              <ul>
                <li>Embedded in the Shopify admin</li>
                <li>Catalogue and order-level context</li>
                <li>Shopify-native billing</li>
              </ul>
              <a className="marketing-button outline-button" href="mailto:support@scaleadsai.com?subject=ScaleAds%20AI%20Shopify%20access">Request Shopify access</a>
              <small>Private Dreamlab Studio app while App Store approval is in progress.</small>
            </article>
            <article className="product-choice featured">
              <div className="choice-label standalone-label">Standalone</div>
              <h3>Connect any storefront</h3>
              <p>Use a first-party pixel and direct ad-account connections on your existing website.</p>
              <ul>
                <li>Independent ScaleAds workspace</li>
                <li>Pixel-based revenue measurement</li>
                <li>Cross-channel recommendations</li>
              </ul>
              <Link className="marketing-button solid-button" to="/standalone">Create standalone account</Link>
              <small>14-day trial. No credit card required.</small>
            </article>
          </div>
        </div>
      </section>

      <section className="marketing-section pricing-section" id="pricing">
        <div className="marketing-wrap">
          <div className="section-heading">
            <p className="marketing-kicker">Flat monthly pricing</p>
            <h2>No percentage of ad spend.</h2>
            <p>Ad spend stays with each platform. ScaleAds is billed as a simple monthly subscription.</p>
          </div>
          <div className="plan-grid">
            {plans.map((plan) => (
              <article className={`plan-card ${plan.featured ? 'featured' : ''}`} key={plan.name}>
                {plan.featured && <span className="plan-ribbon">Most chosen</span>}
                <h3>{plan.name}</h3>
                <p className="plan-price">{plan.price}<small>/month</small></p>
                <p className="plan-trial">14-day free trial</p>
                <ul>{plan.features.map((feature) => <li key={feature}>{feature}</li>)}</ul>
                <Link className={`marketing-button ${plan.featured ? 'solid-button' : 'outline-button'}`} to="/standalone">Start trial</Link>
              </article>
            ))}
          </div>
          <p className="pricing-note">Prices in USD. Upgrades and downgrades are managed inside the product.</p>
        </div>
      </section>

      <section className="marketing-section faq-section" id="faq">
        <div className="marketing-wrap faq-wrap">
          <div className="section-heading">
            <p className="marketing-kicker">Questions worth asking</p>
            <h2>Clear answers before you connect anything.</h2>
          </div>
          <details open><summary>Does ScaleAds spend money without asking?</summary><p>Actions stay inside guardrails you set. Anything above your approval threshold stops and asks first, and every action is logged.</p></details>
          <details><summary>Do Shopify and standalone accounts share data?</summary><p>No. They share one public website and product language, but use separate authentication and backend boundaries.</p></details>
          <details><summary>Why does the measurement differ from an ad dashboard?</summary><p>Platform attribution can overlap. ScaleAds uses first-party revenue context and conservative measurement to help show the difference.</p></details>
          <details><summary>Can I use ScaleAds without Shopify?</summary><p>Yes. The standalone edition works with your existing storefront through a first-party pixel and direct platform connections.</p></details>
        </div>
      </section>

      <section className="marketing-cta">
        <div className="marketing-wrap">
          <p className="marketing-kicker">Ready when you are</p>
          <h2>Make tomorrow’s ad spend clearer than today’s.</h2>
          <div className="hero-actions">
            <Link className="marketing-button hero-primary" to="/standalone">Start standalone</Link>
            <a className="marketing-button hero-secondary" href="mailto:support@scaleadsai.com?subject=ScaleAds%20AI%20Shopify%20access">Request Shopify access</a>
          </div>
        </div>
      </section>

      <footer className="marketing-footer">
        <div className="marketing-wrap footer-inner">
          <Link className="marketing-logo" to="/"><img className="brand-logo-image" src={logoUrl} alt="" aria-hidden="true" />ScaleAds AI</Link>
          <nav aria-label="Footer navigation"><a href="/privacy">Privacy</a><a href="/terms">Terms</a><a href="mailto:support@scaleadsai.com">Support</a></nav>
          <p>Dreamlab Studio · Melbourne, Australia</p>
        </div>
      </footer>
    </main>
  );
}
